<template>
  <v-list nav dense>
    <v-list-item v-if="currentUser" class="green-gradient-down mr-2 mb-8">
      <v-list-item-content class="rounded-md">
        <v-list-item-title class="d-flex align-center justify-space-around">
          <h3 class="font-bukhari text-h4 white--text">{{ $t('label.hello') }}!</h3>
          <span class="text-h5 font-weight-bold yellow--text text--lighten-1">{{ currentUser.full_name }}</span>
        </v-list-item-title>
        <router-link
          v-if="isSGUser"
          class="d-flex align-center justify-space-around white--text my-1 text-decoration-none white--text"
          :to="{ name: $constants.ROUTE_NAME.APPOINTMENT_MY_APPOINTMENT }"
        >
          <span class="mr-2 d-flex align-center">
            <span class="icon-sizing white d-flex justify-center align-center rounded-circle">
              <CalendarIcon />
            </span>
            <div v-if="currentUser.next_appointment_date" class="ml-2">
              <p class="text-mini mb-lg-1 text-truncate">{{ $t('label.upcomingAppointment') }}</p>
              <p class="text-mini my-0">{{ currentUser.next_appointment_date | shortDate }}</p>
            </div>
          </span>
        </router-link>
        <v-list-item-title class="d-flex align-center justify-space-around white--text mt-2" v-if="isSGUser">
          <app-button :label="$t('label.makeAppointment')" class="secondary--text px-lg-7" :to="{ name: $constants.ROUTE_NAME.APPOINTMENT_MAKE_APPOINTMENT }" />
        </v-list-item-title>
      </v-list-item-content>
    </v-list-item>
    <v-list-item-group active-class="primary--text text--accent-4">
      <v-list-item v-if="currentUser && currentUser.roles === 'Agent'" @click="routeTo({ name: $constants.ROUTE_NAME.AGENT_DASHBOARD, query: { tab: 'cash_back' } })">
        <div class="d-flex flex-row align-center justify-start">
          <AgentIcon :level="currentUser.ranking.replace('Style', '').trim().toUpperCase()" small />
          <div class="ml-2">
            {{ agentRankName }}
          </div>
        </div>
      </v-list-item>
      <v-list-item v-if="currentUser" @click="routeTo({ name: $constants.ROUTE_NAME.WALLET })">
        <div class="d-flex flex-row align-center justify-space-between w-100" style="max-width: 300px">
          <div class="d-flex align-center justify-space-between text-capitalize">
            <img height="25px" class="mr-2" src="@/assets/coin.png" />
            {{ currentUser.point }} {{ $t('label.points') }}
          </div>
          <div class="d-flex align-center text-capitalize" v-if="isSGUser">
            <img height="16px" class="mr-2" src="@/assets/voucher.png" />
            {{ currentUser.voucher_count }} {{ $t('label.vouchers') }}
          </div>
        </div>
      </v-list-item>
      <v-list-group :value="true" v-if="currentUser">
        <template v-slot:activator>
          <v-list-item-content>
            <v-list-item-title>{{ $t('label.profile') }}</v-list-item-title>
          </v-list-item-content>
        </template>
        <v-list-item link @click="routeTo({ name: $constants.ROUTE_NAME.PROFILE })">
          <v-list-item-title v-text="$t('label.myProfile')"></v-list-item-title>
        </v-list-item>
        <v-list-item link @click="routeTo({ name: $constants.ROUTE_NAME.PRODUCT_REVIEW })">
          <v-list-item-title v-text="$t('label.productReview')"></v-list-item-title>
        </v-list-item>
        <v-list-item link @click="routeTo({ name: $constants.ROUTE_NAME.SALON_STYLIST_REVIEW_LIST })">
          <v-list-item-title v-text="$t('label.salonStylistReview')"></v-list-item-title>
        </v-list-item>
      </v-list-group>
      <div v-if="!currentUser">
        <v-list-item @click.prevent="openLoginDialog">
          <v-list-item-title>{{ $t('label.login') }}</v-list-item-title>
        </v-list-item>
        <v-list-item @click.prevent="openSignUpDialog">
          <v-list-item-title>{{ $t('label.signUp') }}</v-list-item-title>
        </v-list-item>
      </div>
      <v-list-item v-if="currentUser" @click="routeTo({ name: $constants.ROUTE_NAME.ORDER_DASHBOARD })">
        <v-list-item-title>{{ $t('label.myOrder') }}</v-list-item-title>
      </v-list-item>
      <v-list-item @click="navigateTo($constants.ROUTE_NAME.PRODUCT_WISH_LIST)">
        <v-list-item-title>{{ $t('label.wishlist') }}</v-list-item-title>
      </v-list-item>
      <v-list-item @click="navigateTo($constants.ROUTE_NAME.ORDER_CART)">
        <v-list-item-title>{{ $t('label.cart') }}</v-list-item-title>
      </v-list-item>
      <v-list-group :value="true">
        <template v-slot:activator>
          <v-list-item-title>{{ $t('label.categories') }}</v-list-item-title>
        </template>

        <v-list-group no-action sub-group v-for="(category, i) in categories" :key="i">
          <template v-slot:activator>
            <v-list-item-content>
              <v-list-item-title>{{ category.name }}</v-list-item-title>
            </v-list-item-content>
          </template>
          <v-list-item
            v-for="(subcategory, i) in category.subcategories"
            :key="i"
            @click="routeTo({ name: $constants.ROUTE_NAME.PRODUCT_CATEGORY, params: { category: category.name.toLowerCase() }, query: { subcategory: subcategory.name.toLowerCase(), brand: 'all' } })"
            link
            exact
          >
            <v-list-item-title v-text="subcategory.name"></v-list-item-title>
          </v-list-item>
        </v-list-group>
      </v-list-group>
      <v-list-item v-if="isSGUser || !currentUser" @click="routeTo({ name: $constants.ROUTE_NAME.SALON_INFO_LIST })">
        <v-list-item-title>{{ $t('label.ourStores') }}</v-list-item-title>
      </v-list-item>
      <a href="#download-app" class="text-decoration-none" style="color: inherit">
        <v-list-item>
          <v-list-item-title>
            {{ $t('button.getTheApp') }}
          </v-list-item-title>
        </v-list-item>
      </a>
      <v-list-item @click="logout" v-if="currentUser">
        <v-list-item-title>
          {{ $t('label.logOut') }}
        </v-list-item-title>
      </v-list-item>
    </v-list-item-group>
  </v-list>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import AgentIcon from '@/components/AgentIcon.vue'
import CalendarIcon from '@/assets/icons/calendar.svg'
import { localeHelper } from '@/utils'

export default {
  components: { AgentIcon, CalendarIcon },
  data: () => ({
    loggingOut: false
  }),
  computed: {
    ...mapState('auth', ['currentUser']),
    ...mapGetters('auth', ['isSGUser']),
    ...mapGetters('page', ['categories']),
    agentRankName() {
      let name = ''
      let rank = this.currentUser.ranking.replace('Style', '').trim().toLowerCase()
      switch (rank) {
        case 'silver':
          name = localeHelper.getMessage('label.silver')
          break
        case 'gold':
          name = localeHelper.getMessage('label.gold')
          break
        case 'platinum':
          name = localeHelper.getMessage('label.platinum')
          break
        case 'executive':
          name = localeHelper.getMessage('label.executive')
          break
      }
      return name
    }
  },
  methods: {
    ...mapActions('auth', { logoutAction: 'logout' }),
    openLoginDialog() {
      this.$root.$app.openLoginDialog()
    },
    openSignUpDialog() {
      this.$root.$app.openSignUpDialog()
    },
    async logout() {
      this.loggingOut = true
      try {
        await this.logoutAction()
      } finally {
        this.loggingOut = false
        this.$router.push({ name: this.$constants.ROUTE_NAME.HOME })
        location.reload()
        this.$emit('close')
      }
    },
    routeTo(routeDetail) {
      console.log('routeDetail', routeDetail)
      this.$router.push(routeDetail)
      this.$emit('close')
    },
    navigateTo(path) {
      if (this.currentUser) this.$router.push({ name: path })
      else this.$root.$app.openLoginDialog()
    },
    test() {
      console.log('PRINT TEST')
    }
  }
}
</script>

<style lang="scss" scoped>
.font-bukhari {
  font-family: 'BukhariScript' !important;
}

.icon-sizing {
  width: 40px;
  height: 40px;
}
</style>
